import { Dispatch, SetStateAction, useEffect, useMemo } from "react"

import { MeetingServiceOrder } from "../../../../modules/merchant/accounting/accounting/accountingTypes"
import {
    SelectedMainUserState,
    CreateOrderDialogFlow,
    CreateOrderDialogMode,
    CreateOrderDialogType,
    AvailableModules,
} from "../createOrderTypes"
import { OutlookBookingSuccessArgs } from "../../../../modules/order/pages/outlookCreateOrderTypes"
import { getUserDetails, GetUserDetailsResponse } from "../../../../modules/users/userApi"
import { createOrderButtonConfig, CreateOrderType, MEETING_SERVICE_SUPERUSER_TYPES } from "../helpers"
import { ModuleName } from "../../../../modules/users/userTypes"
import { UserType } from "../../../../modules/login/loginTypes"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import { getCreateOrderDialogTypeFlags } from "../hooks/useCreateOrderDialogType"
import { findOrganizerAddress } from "../../../../modules/order/pages/helpers"
import { getOrganizers } from "../../../utils/getOrderPerson"
import { CreateOrderFormProvider } from "./CreateOrderFormContext"
import { OrderFormController } from "./OrderFormController"
import { ConfigQuery } from "../createOrderApi"
import { OrderContextState } from "./helpers"

const transformData = (data: GetUserDetailsResponse) => data.user

type CreateOrderDialogContentProps = Readonly<{
    handleClose: () => void
    user: SelectedMainUserState
    activeOrderTab?: CreateOrderType
    dialogType: CreateOrderDialogType
    handleInvalidUserPermissions: () => void
    flowConfig?: CreateOrderDialogFlow
    orderContext: OrderContextState
    setOrderContext: Dispatch<SetStateAction<OrderContextState>>
    switchToOrder: (orderId: number) => void
    orderDetails?: MeetingServiceOrder
    handleOutlookBookingCompleted?: (args: OutlookBookingSuccessArgs) => void
}>

export function CreateOrderDialogContent({
    handleClose,
    user,
    activeOrderTab = CreateOrderType.RESOURCE_BOOKING,
    dialogType,
    handleInvalidUserPermissions,
    flowConfig,
    orderContext,
    setOrderContext,
    switchToOrder,
    orderDetails,
    handleOutlookBookingCompleted,
}: CreateOrderDialogContentProps) {
    const { isRegularOrder } = getCreateOrderDialogTypeFlags(dialogType)

    const { data: userDetails, isSuccess: isUserDetailsSuccess } = useApiQuery(getUserDetails, {
        queryName: ConfigQuery.USER_DETAILS,
        dependencies: { userId: user.userId },
        suspense: true,
        useErrorBoundary: true,
        select: transformData,
        enabled: !!user.userId,
    })
    const availableModules: AvailableModules = useMemo(() => {
        const bookingSuperuserTypes: ReadonlyArray<UserType> = [
            UserType.COMPANY,
            UserType.SERVICE_CENTER,
            UserType.SUPPORT,
        ]
        const isNonCreate = orderContext.mode !== CreateOrderDialogMode.CREATE
        const isAdminNonCreate = isNonCreate && MEETING_SERVICE_SUPERUSER_TYPES.includes(user.userType)
        const isBookingAdminNonCreate = isNonCreate && bookingSuperuserTypes.includes(user.userType)

        if (!userDetails?.modules) {
            const queryDoneWithNoModules = isUserDetailsSuccess || !user.userId
            if (queryDoneWithNoModules) {
                return {
                    isBookingActive: isBookingAdminNonCreate,
                    isCateringActive: isAdminNonCreate,
                }
            }
            return { isBookingActive: false, isCateringActive: false }
        }

        const isBookingActive =
            userDetails.modules.some((m) => m.name === ModuleName.BOOKING && m.isActive) || isBookingAdminNonCreate
        const isCateringActive =
            userDetails.modules.some((m) => m.name === ModuleName.CATERING && m.isActive) || isAdminNonCreate

        return { isBookingActive, isCateringActive }
    }, [orderContext.mode, user.userId, user.userType, isUserDetailsSuccess, userDetails?.modules])

    const filteredTabButtonConfig = createOrderButtonConfig.reduce<typeof createOrderButtonConfig>((acc, button) => {
        if (button.value === CreateOrderType.MEETING_SERVICE) {
            acc.push({ ...button, disabled: isRegularOrder ? !availableModules.isCateringActive : false })
        } else if (button.value === CreateOrderType.RESOURCE_BOOKING && isRegularOrder) {
            acc.push({
                ...button,
                disabled: !!flowConfig?.vstoFlowParam || !availableModules.isBookingActive,
            })
        }
        return acc
    }, [])

    const initialOrderType: CreateOrderType = (() => {
        if (!isRegularOrder) {
            return CreateOrderType.MEETING_SERVICE
        }

        const isInitialActive = filteredTabButtonConfig.some((btn) => btn.value === activeOrderTab && !btn.disabled)
        if (isInitialActive) return activeOrderTab

        const firstActive = filteredTabButtonConfig.find((btn) => !btn.disabled)
        return firstActive ? firstActive.value : activeOrderTab
    })()

    const hasActiveTabs = filteredTabButtonConfig.some((btn) => !btn.disabled)

    // A stored pattern, not the calendar id, is what makes a recurrence Outlook-managed.
    const orderRecurrencePattern = orderDetails?.meetingDetails?.recurrence?.pattern
    // Resolved here because this is the last place that still has the order itself: the confirm
    // dialogs below only receive the form values. `getOrganizers` because the array lives at the
    // root on a booking but under `meetingDetails` on a meeting-service order — reading only one
    // of the two would lose the calendar and revoke the workgroup's access on the next save.
    const savedOrganizers = orderDetails ? getOrganizers(orderDetails) : undefined
    const calendarOrganizer = findOrganizerAddress(savedOrganizers)
    const customerId = orderDetails?.customer?.id
    const orderPeople = useMemo(() => ({ customerId, organizers: savedOrganizers }), [customerId, savedOrganizers])

    const config = useMemo(
        () => ({
            user,
            dialogType,
            flowConfig,
            orderRecurrencePattern,
            calendarOrganizer,
            orderPeople,
            availableModules,
            handleClose,
            handleOutlookBookingCompleted,
        }),
        [
            user,
            dialogType,
            flowConfig,
            orderRecurrencePattern,
            calendarOrganizer,
            orderPeople,
            availableModules,
            handleClose,
            handleOutlookBookingCompleted,
        ],
    )

    const state = useMemo(() => ({ orderContext, setOrderContext }), [orderContext, setOrderContext])

    useEffect(() => {
        if (!hasActiveTabs) {
            handleInvalidUserPermissions()
        }
    }, [hasActiveTabs, handleInvalidUserPermissions])

    if (!hasActiveTabs) {
        return <></>
    }

    return (
        <CreateOrderFormProvider config={config} state={state} switchToOrder={switchToOrder}>
            <OrderFormController
                initialOrderType={initialOrderType}
                filteredTabButtonConfig={filteredTabButtonConfig}
                orderDetails={orderDetails}
            />
        </CreateOrderFormProvider>
    )
}
